import {useId, useLayoutEffect, useMemo} from 'react'

import {useArchitecture} from '../../lib/hooks/use-architecture'
import {useDetectedMobileOS, useDetectedOS} from '../../lib/hooks/use-detected-os'
import {
  resolveDownloadActions,
  type OSAwareDownloadAction,
  type ResolvedDownloadActions,
} from './resolve-download-actions'

export type OSAwareDownloadActionMenuController = ResolvedDownloadActions & {
  actionMenuId: string
}

export function useOSAwareDownloadActionMenu(
  actions?: readonly OSAwareDownloadAction[],
  menuLabel?: string,
): OSAwareDownloadActionMenuController | undefined {
  const detectedOS = useDetectedOS()
  const detectedMobileOS = useDetectedMobileOS()
  const architecture = useArchitecture(Boolean(actions?.length))
  const actionMenuId = useId()

  useLayoutEffect(() => {
    if (!actions?.length || !menuLabel) return

    const toggle = document.getElementById(`${actionMenuId}-button`)
    if (!(toggle instanceof HTMLButtonElement)) return

    const previousLabel = toggle.getAttribute('aria-label')
    toggle.setAttribute('aria-label', menuLabel)

    return () => {
      if (previousLabel === null) toggle.removeAttribute('aria-label')
      else toggle.setAttribute('aria-label', previousLabel)
    }
  }, [actionMenuId, actions, menuLabel])

  return useMemo(() => {
    if (!actions?.length) return undefined

    return {
      ...resolveDownloadActions(
        actions as readonly [OSAwareDownloadAction, ...OSAwareDownloadAction[]],
        detectedMobileOS ?? detectedOS,
        architecture,
      ),
      actionMenuId,
    }
  }, [actionMenuId, actions, architecture, detectedMobileOS, detectedOS])
}
